import React, { useState, useMemo, useEffect } from 'react';
import {
  Flower2,
  Sparkles,
  Check,
  RotateCcw,
  Palette,
  Save,
  CheckCircle2,
  Wand2,
} from 'lucide-react';
import { CoupleSettings, TulipSettings } from '../../types';
import {
  TULIP_COLOR_PRESETS,
  DEFAULT_TULIP_INDIVIDUAL_COLORS,
  resolveTulipColor,
} from '../../utils/tulipColorUtils';
import { FlowerGraphic } from '../FlowerGraphic';
import { UniversalColorPalette } from './UniversalColorPalette';

interface TulipAdminSectionProps {
  settings: CoupleSettings;
  onSaveSettings: (newSettings: CoupleSettings) => void;
  showCardWrapper?: boolean;
}

export const TulipAdminSection: React.FC<TulipAdminSectionProps> = ({
  settings,
  onSaveSettings,
  showCardWrapper = true,
}) => {
  const [mode, setMode] = useState<'all' | 'individual'>(() => {
    return settings.tulipSettings?.mode || 'individual';
  });

  const [allColor, setAllColor] = useState<string>(() => {
    return settings.tulipSettings?.allColor || 'golden';
  });

  const [individualColors, setIndividualColors] = useState<string[]>(() => {
    if (settings.tulipSettings?.individualColors && settings.tulipSettings.individualColors.length === 4) {
      return [...settings.tulipSettings.individualColors];
    }
    return [...DEFAULT_TULIP_INDIVIDUAL_COLORS];
  });

  const [selectedTulipIndex, setSelectedTulipIndex] = useState<number>(0);
  const [customHex, setCustomHex] = useState<string>('#f43f5e');
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  // Sync state if settings prop changes from outside
  useEffect(() => {
    if (settings.tulipSettings) {
      if (settings.tulipSettings.mode) setMode(settings.tulipSettings.mode);
      if (settings.tulipSettings.allColor) setAllColor(settings.tulipSettings.allColor);
      if (settings.tulipSettings.individualColors && settings.tulipSettings.individualColors.length === 4) {
        setIndividualColors([...settings.tulipSettings.individualColors]);
      }
    }
  }, [settings.tulipSettings]);

  // Current active color being modified
  const currentActiveColor = useMemo(() => {
    if (mode === 'all') {
      return allColor;
    }
    return individualColors[selectedTulipIndex] || 'golden';
  }, [mode, allColor, individualColors, selectedTulipIndex]);

  // Resolve current active color to a HEX representation
  const activeHex = useMemo(() => {
    if (currentActiveColor.startsWith('#')) return currentActiveColor;
    const preset = TULIP_COLOR_PRESETS.find((p) => p.id === currentActiveColor);
    return preset ? preset.hex : '#f5b000';
  }, [currentActiveColor]);

  // Computed tulip settings for live preview
  const previewTulipSettings = useMemo<TulipSettings>(() => {
    return {
      mode,
      allColor,
      individualColors,
    };
  }, [mode, allColor, individualColors]);

  // Handle color selection
  const handleSelectColor = (colorIdOrHex: string) => {
    setHasChanges(true);
    setSaveSuccess(false);

    if (mode === 'all') {
      setAllColor(colorIdOrHex);
    } else {
      setIndividualColors((prev) => {
        const next = [...prev];
        next[selectedTulipIndex] = colorIdOrHex;
        return next;
      });
    }
  };

  // Apply current active color to ALL tulips
  const handleApplyCurrentToAll = () => {
    setHasChanges(true);
    setSaveSuccess(false);
    const targetColor = currentActiveColor;
    setAllColor(targetColor);
    setIndividualColors([targetColor, targetColor, targetColor, targetColor]);
  };

  // Reset to natural default
  const handleResetToDefault = () => {
    setHasChanges(true);
    setSaveSuccess(false);
    setMode('individual');
    setAllColor('golden');
    setIndividualColors([...DEFAULT_TULIP_INDIVIDUAL_COLORS]);
  };

  // Save changes to settings
  const handleSave = () => {
    const updatedTulipSettings: TulipSettings = {
      mode,
      allColor,
      individualColors,
    };

    const updatedSettings: CoupleSettings = {
      ...settings,
      tulipSettings: updatedTulipSettings,
    };

    onSaveSettings(updatedSettings);
    setHasChanges(false);
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
    }, 4000);
  };

  const content = (
    <div className="space-y-6">
      {/* Header section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-pink-500/10 to-amber-500/10 border border-pink-200 text-pink-600 flex items-center justify-center shrink-0">
            <Flower2 className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-base sm:text-lg text-slate-900 flex items-center gap-2">
              <span>Color de los Tulipanes del Jardín 🌷</span>
            </h3>
            <p className="text-xs text-slate-500">
              Personaliza el color de cada uno de los 4 tulipanes por separado o de todas a la vez.
            </p>
          </div>
        </div>

        <span className="px-3 py-1 rounded-full text-xs font-bold bg-pink-50 text-pink-700 border border-pink-200 shrink-0 self-start sm:self-center">
          {mode === 'all' ? '✨ Todas a la vez' : '🎨 Cada una por separado'}
        </span>
      </div>

      {/* 1. LIVE MEADOW PREVIEW (PRADO VIRTUAL EN VIVO) */}
      <div className="rounded-2xl p-4 sm:p-5 bg-gradient-to-b from-sky-50/70 via-emerald-50/40 to-emerald-100/60 border border-emerald-200/80 shadow-xs relative overflow-hidden">
        <div className="flex items-center justify-between pb-3 mb-2 border-b border-emerald-200/60 text-xs">
          <span className="text-slate-800 font-bold flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
            <span>Vista Previa en Tiempo Real del Jardín</span>
          </span>
          <span className="text-[11px] text-slate-500">
            {mode === 'all' ? 'Toca cualquier tulipán para verlo' : 'Haz clic en un tulipán para editar su color'}
          </span>
        </div>

        {/* The 4 Tulips in the meadow */}
        <div className="relative pt-4 pb-2 px-2 sm:px-6 flex items-end justify-around max-w-xl mx-auto min-h-[170px]">
          {[0, 1, 2, 3].map((idx) => {
            const resolved = resolveTulipColor(previewTulipSettings, idx);
            const isSelected = mode === 'individual' && selectedTulipIndex === idx;

            return (
              <div
                key={idx}
                onClick={() => {
                  if (mode === 'individual') {
                    setSelectedTulipIndex(idx);
                  }
                }}
                className={`flex flex-col items-center cursor-pointer transition-transform group ${
                  isSelected ? 'scale-105' : 'hover:scale-102'
                }`}
              >
                {/* Tulip flower graphic scaled down */}
                <div className="relative origin-bottom transform scale-[0.62] sm:scale-[0.72] -mb-4">
                  <FlowerGraphic
                    index={idx}
                    colorValue={resolved.colorValue}
                    isSelected={isSelected}
                    onClick={() => {
                      if (mode === 'individual') setSelectedTulipIndex(idx);
                    }}
                  />
                </div>

                {/* Tulip indicator tag */}
                <div
                  className={`mt-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold transition-all flex items-center gap-1 shadow-xs ${
                    isSelected
                      ? 'bg-slate-900 text-white ring-2 ring-slate-400/40 scale-105'
                      : 'bg-white/95 text-slate-700 border border-slate-200 hover:border-emerald-400 hover:text-slate-900'
                  }`}
                >
                  <span>Tulipán {idx + 1}</span>
                  {isSelected && <Check className="w-2.5 h-2.5 stroke-[3] text-amber-400" />}
                </div>
              </div>
            );
          })}
        </div>

        {/* Meadow base strip */}
        <div className="h-2.5 w-full bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 rounded-full mt-2 shadow-2xs" />
      </div>

      {/* 2. MODE SELECTOR (TODAS O CADA UNA) */}
      <div className="space-y-3">
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-600">
          ¿Cómo deseas cambiar los colores?
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Mode 1: All together */}
          <button
            type="button"
            onClick={() => {
              setMode('all');
              setHasChanges(true);
              setSaveSuccess(false);
            }}
            className={`p-4 rounded-2xl border text-left transition-all cursor-pointer flex flex-col justify-between space-y-2 ${
              mode === 'all'
                ? 'bg-gradient-to-br from-pink-50 to-rose-50/50 border-pink-300 ring-2 ring-pink-300/40 shadow-xs'
                : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-2xl">🌐</span>
              {mode === 'all' && (
                <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase bg-pink-500 text-white">
                  Activo
                </span>
              )}
            </div>
            <div>
              <h4 className="font-bold text-sm text-slate-900">Todas a la vez (Unificado)</h4>
              <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                Aplica un mismo color armónico a todos los 4 tulipanes del jardín simultáneamente.
              </p>
            </div>
          </button>

          {/* Mode 2: Individual */}
          <button
            type="button"
            onClick={() => {
              setMode('individual');
              setHasChanges(true);
              setSaveSuccess(false);
            }}
            className={`p-4 rounded-2xl border text-left transition-all cursor-pointer flex flex-col justify-between space-y-2 ${
              mode === 'individual'
                ? 'bg-gradient-to-br from-amber-50 to-pink-50/50 border-amber-300 ring-2 ring-amber-300/40 shadow-xs'
                : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-2xl">🌷</span>
              {mode === 'individual' && (
                <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase bg-amber-500 text-white">
                  Activo
                </span>
              )}
            </div>
            <div>
              <h4 className="font-bold text-sm text-slate-900">Cada una por separado</h4>
              <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                Elige un color diferente e independiente para cada uno de los 4 tulipanes.
              </p>
            </div>
          </button>
        </div>
      </div>

      {/* 3. INDIVIDUAL TULIP SELECTOR TABS (Shown when in 'individual' mode) */}
      {mode === 'individual' && (
        <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
              <span>Selecciona qué tulipán deseas editar:</span>
            </label>
            <button
              type="button"
              onClick={handleApplyCurrentToAll}
              className="text-[11px] font-semibold text-pink-600 hover:text-pink-700 underline decoration-pink-300 cursor-pointer flex items-center gap-1"
              title="Copiar el color de este tulipán a los otros 3"
            >
              <Wand2 className="w-3 h-3" />
              <span>Copiar este color a todas</span>
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[0, 1, 2, 3].map((idx) => {
              const isSelected = selectedTulipIndex === idx;
              const colorVal = individualColors[idx] || 'golden';
              const preset = TULIP_COLOR_PRESETS.find((p) => p.id === colorVal);
              const colorDisplay = preset ? preset.hex : colorVal;
              const colorName = preset ? preset.name : colorVal;

              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setSelectedTulipIndex(idx)}
                  className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer flex items-center gap-2.5 ${
                    isSelected
                      ? 'bg-amber-50 border-amber-400 ring-2 ring-amber-300 shadow-xs'
                      : 'bg-white border-slate-200 hover:bg-slate-100 hover:border-slate-300'
                  }`}
                >
                  <div
                    className="w-5 h-5 rounded-full border border-slate-300 shadow-xs shrink-0"
                    style={{ backgroundColor: colorDisplay }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-bold text-slate-900 truncate">
                      Tulipán {idx + 1}
                    </div>
                    <div className="text-[10px] text-slate-500 truncate">
                      {colorName}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* 4. COLOR PALETTE SWATCHES */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-600">
            {mode === 'all'
              ? 'Selecciona el color para todas las flores:'
              : `Selecciona el color para el Tulipán ${selectedTulipIndex + 1}:`}
          </label>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-5 gap-2.5">
          {TULIP_COLOR_PRESETS.map((preset) => {
            const isSelected = currentActiveColor === preset.id;

            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => handleSelectColor(preset.id)}
                className={`p-3 rounded-xl border transition-all cursor-pointer flex flex-col items-center text-center gap-2 group ${
                  isSelected
                    ? 'bg-amber-50 border-amber-400 ring-2 ring-amber-300 shadow-xs'
                    : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                }`}
              >
                {/* Visual colored circle with petal gradient */}
                <div
                  className="w-8 h-8 rounded-full border-2 border-white shadow-md flex items-center justify-center transition-transform group-hover:scale-110"
                  style={{
                    backgroundColor: preset.hex,
                    backgroundImage: `linear-gradient(135deg, ${preset.vars['--gold-2']}, ${preset.vars['--gold-4']})`,
                  }}
                >
                  {isSelected && <Check className="w-4 h-4 text-white stroke-[3] drop-shadow-md" />}
                </div>

                <div className="text-[11px] font-bold text-slate-800 leading-tight">
                  {preset.name}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 5. PALETA UNIVERSAL DE TODOS LOS COLORES EXISTENTES (ESPECTRO CONTINUO 360° + CATÁLOGO) */}
      <UniversalColorPalette
        currentColor={activeHex}
        onColorSelect={(newHex) => {
          setCustomHex(newHex);
          handleSelectColor(newHex);
        }}
        targetLabel={mode === 'all' ? 'todas las flores' : `Tulipán ${selectedTulipIndex + 1}`}
        onApplyToAll={handleApplyCurrentToAll}
      />

      {/* SUCCESS BANNER */}
      {saveSuccess && (
        <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-xs font-bold flex items-center gap-2 animate-fadeIn shadow-2xs">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>
            ¡Colores de los tulipanes guardados exitosamente! Ya se aprecian en el jardín interactivo.
          </span>
        </div>
      )}

      {/* 7. ACTION FOOTER BUTTONS */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-200">
        <button
          type="button"
          onClick={handleResetToDefault}
          className="w-full sm:w-auto px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl border border-slate-200 transition-all cursor-pointer flex items-center justify-center gap-2"
        >
          <RotateCcw className="w-4 h-4 text-slate-500" />
          <span>Restablecer a Colores Originales</span>
        </button>

        <button
          type="button"
          onClick={handleSave}
          className="w-full sm:w-auto px-6 py-2.5 bg-gradient-to-r from-pink-500 via-rose-500 to-amber-500 hover:from-pink-600 hover:to-amber-600 text-white font-bold text-xs sm:text-sm rounded-xl shadow-md transition-all hover:scale-102 active:scale-98 cursor-pointer flex items-center justify-center gap-2"
        >
          <Save className="w-4 h-4" />
          <span>Guardar Colores de los Tulipanes</span>
          {hasChanges && (
            <span className="w-2 h-2 rounded-full bg-amber-300 animate-pulse" />
          )}
        </button>
      </div>
    </div>
  );

  if (!showCardWrapper) {
    return content;
  }

  return (
    <div className="bg-white p-5 sm:p-7 rounded-2xl border border-slate-200 shadow-xs max-w-3xl">
      {content}
    </div>
  );
};
